import { action, computed, makeObservable, reaction } from 'mobx';

import { AlertsStore } from '@/configs/store/AlertsStore';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { trackerApi } from '@/tracker/api/trackerApi';
import { matchesQuery } from '@/tracker/entities/issue';
import { CLOSED_STATUS_TYPES } from '@/tracker/entities/status';
import { TrackerStore } from '@/tracker/model';
import { CreateSprintRequest, Sprint, Task, TaskBasic, UpdateSprintRequest } from '@/tracker/types';
import { showError } from '@/tracker/utils/showError';

import { BoardLayoutStore } from './BoardLayoutStore';

/** id колонки бэклога; у колонок спринтов — id спринта */
export const BACKLOG_ID = 'backlog';

export type BacklogColumn = { id: string; sprint: Sprint | null; tasks: TaskBasic[] };

/** Сначала идущий спринт, потом черновики по дате начала — как в бэклоге Яндекс Трекера */
const sprintOrder = (a: Sprint, b: Sprint) =>
  Number(b.status === 'active') - Number(a.status === 'active') ||
  a.startDate.localeCompare(b.startDate);

/** Вкладка «Бэклог» доски: задачи без спринта и открытые спринты, управление спринтами */
export class BacklogStore extends LocalStore {
  private readonly _alertsStore: AlertsStore;
  private readonly _trackerStore: TrackerStore;
  private readonly _layout: BoardLayoutStore;
  private readonly _boardId: string;
  /** Открытые спринты доски: идущий и черновики */
  private readonly _sprints = new ValueModel<Sprint[]>([]);
  /** Задачи по колонкам: BACKLOG_ID или id спринта */
  private readonly _tasks = new ValueModel<Record<string, TaskBasic[]>>({});
  private readonly _loadingStage = new LoadingStageModel();
  private readonly _contentStage = new LoadingStageModel();
  private readonly _savingStage = new LoadingStageModel();
  /** Ответ на устаревший запрос не должен перетереть свежий */
  private _contentRequest = 0;

  constructor({
    alertsStore,
    trackerStore,
    layout,
  }: {
    alertsStore: AlertsStore;
    trackerStore: TrackerStore;
    /** Доска уже загружена: вкладка показывается под шапкой после неё */
    layout: BoardLayoutStore;
  }) {
    super();
    this._alertsStore = alertsStore;
    this._trackerStore = trackerStore;
    this._layout = layout;
    this._boardId = layout.boardId;

    makeObservable(this, {
      sprints: computed,
      columns: computed,
      hasActiveSprint: computed,
      canManage: computed,
      isError: computed,
      isContentLoading: computed,
      isSaving: computed,
      load: action.bound,
      moveTask: action.bound,
      createSprint: action.bound,
      updateSprint: action.bound,
      deleteSprint: action.bound,
      startSprint: action.bound,
      closeSprint: action.bound,
    });
  }

  get sprints() {
    return this._sprints.value;
  }

  /** Колонки с задачами под поиском */
  get columns(): BacklogColumn[] {
    return [
      { id: BACKLOG_ID, sprint: null, tasks: this.visibleTasks(BACKLOG_ID) },
      ...this._sprints.value.map((sprint) => ({
        id: sprint.id,
        sprint,
        tasks: this.visibleTasks(sprint.id),
      })),
    ];
  }

  /** Сколько незакрытых задач переедет при завершении спринта — по всем, а не только найденным */
  unfinishedCount(sprintId: string) {
    return (this._tasks.value[sprintId] ?? []).filter(
      (task) => !CLOSED_STATUS_TYPES.includes(task.status.type),
    ).length;
  }

  get hasActiveSprint() {
    return this._sprints.value.some((sprint) => sprint.status === 'active');
  }

  get canManage() {
    return this._layout.canManage;
  }

  get isError() {
    return this._loadingStage.isError;
  }

  get isContentLoading() {
    return this._contentStage.isLoading;
  }

  get isSaving() {
    return this._savingStage.isLoading;
  }

  async load() {
    try {
      this._loadingStage.loading();
      await this.loadSprints();
      this._loadingStage.success();
    } catch (error) {
      this._loadingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось загрузить спринты', error });

      return;
    }

    this.loadTasks();
    this.addReaction(
      reaction(
        () => this._trackerStore.updatedIssue,
        (updated) => updated && this.applyUpdatedIssue(updated),
      ),
    );
  }

  /** Перенос задачи между бэклогом и спринтами; на экране — сразу, при ошибке колонки перезагружаются */
  async moveTask({
    key,
    from,
    to,
    index,
  }: {
    key: string;
    from: string;
    to: string;
    index: number;
  }) {
    if (from === to) return;

    const tasks = { ...this._tasks.value };
    const task = (tasks[from] ?? []).find((item) => item.key === key);

    if (!task) return;

    // index — место среди видимых под поиском: встаём перед задачей, которая там была
    const before = this.visibleTasks(to)[index];

    tasks[from] = tasks[from].filter((item) => item.key !== key);
    tasks[to] = [...(tasks[to] ?? [])];
    tasks[to].splice(before ? tasks[to].indexOf(before) : tasks[to].length, 0, task);
    this._tasks.change(tasks);

    try {
      // В спринт — :addTasks (из другого спринта доски задача уходит сама), в бэклог — :removeTasks
      if (to === BACKLOG_ID) {
        await trackerApi.removeTasksFromSprint({
          boardId: this._boardId,
          sprintId: from,
          keys: [key],
        });
      } else {
        await trackerApi.addTasksToSprint({ boardId: this._boardId, sprintId: to, keys: [key] });
      }
    } catch (error) {
      showError({ alertsStore: this._alertsStore, title: `Не удалось перенести ${key}`, error });
      this.loadTasks();
    }
  }

  createSprint(data: CreateSprintRequest) {
    return this.mutate({
      title: 'Не удалось создать спринт',
      run: () => trackerApi.createSprint({ boardId: this._boardId, data }),
    });
  }

  updateSprint({ sprintId, data }: { sprintId: string; data: UpdateSprintRequest }) {
    return this.mutate({
      title: 'Не удалось сохранить спринт',
      run: () => trackerApi.updateSprint({ boardId: this._boardId, sprintId, data }),
    });
  }

  deleteSprint(sprintId: string) {
    return this.mutate({
      title: 'Не удалось удалить спринт',
      run: () => trackerApi.deleteSprint({ boardId: this._boardId, sprintId }),
    });
  }

  startSprint(sprintId: string) {
    return this.mutate({
      title: 'Не удалось запустить спринт',
      run: () => trackerApi.startSprint({ boardId: this._boardId, sprintId }),
    });
  }

  closeSprint({
    sprintId,
    moveUnfinishedTo,
  }: {
    sprintId: string;
    moveUnfinishedTo: string | null;
  }) {
    return this.mutate({
      title: 'Не удалось завершить спринт',
      run: () => trackerApi.closeSprint({ boardId: this._boardId, sprintId, moveUnfinishedTo }),
    });
  }

  /** Действие со спринтом, затем свежие спринты, доска (её текущий спринт) и задачи. true — получилось */
  private async mutate({ title, run }: { title: string; run: () => Promise<unknown> }) {
    try {
      this._savingStage.loading();
      await run();

      const [board] = await Promise.all([trackerApi.getBoard(this._boardId), this.loadSprints()]);

      this._layout.setBoard(board);
      this._savingStage.success();
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title, error });

      return false;
    }

    this.loadTasks();

    return true;
  }

  private async loadSprints() {
    const sprints = await trackerApi.searchAllSprints({
      boardId: this._boardId,
      request: { filters: { status: { in: ['active', 'draft'] } } },
    });

    this._sprints.change([...sprints].sort(sprintOrder));
  }

  private async loadTasks() {
    const request = ++this._contentRequest;
    const sprintIds = this._sprints.value.map((sprint) => sprint.id);

    try {
      this._contentStage.loading();

      const [backlog, inSprints] = await Promise.all([
        // Бэклог доски — её задачи без спринта на этой доске
        trackerApi.searchAllTasks({
          filters: { board: { eq: this._boardId }, sprint: { isNull: true } },
        }),
        // FULL — нужны спринты задачи, чтобы разложить по колонкам
        sprintIds.length
          ? (trackerApi.searchAllTasks({
              filters: { sprint: { in: sprintIds } },
              view: 'FULL',
            }) as Promise<Task[]>)
          : Promise.resolve([]),
      ]);

      if (request !== this._contentRequest) return;

      const tasks: Record<string, TaskBasic[]> = { [BACKLOG_ID]: backlog };

      sprintIds.forEach((id) => {
        tasks[id] = inSprints.filter((task) => task.sprints.some((sprint) => sprint.id === id));
      });
      this._tasks.change(tasks);
      this._contentStage.success();
    } catch (error) {
      if (request !== this._contentRequest) return;

      this._contentStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось загрузить задачи', error });
    }
  }

  /**
   * Задачу поменяли в карточке. Осталась в своей колонке — меняем строку на месте; спринт или доску
   * сменили — перезагружаем колонки. Чужие задачи не трогаем
   */
  private applyUpdatedIssue(updated: Task) {
    const current = Object.keys(this._tasks.value).find((columnId) =>
      this._tasks.value[columnId].some((task) => task.key === updated.key),
    );
    const expected = this.columnOf(updated);

    if (current === undefined && expected === null) return;

    if (current !== expected) {
      this.loadTasks();

      return;
    }

    this._tasks.change({
      ...this._tasks.value,
      [current]: this._tasks.value[current].map((task) =>
        task.key === updated.key ? updated : task,
      ),
    });
  }

  /** Колонка задачи: открытый спринт этой доски, бэклог доски или null — задача не отсюда */
  private columnOf(task: Task): string | null {
    const sprint = task.sprints.find((item) =>
      this._sprints.value.some((open) => open.id === item.id),
    );

    if (sprint) return sprint.id;

    return task.boards.some((board) => board.id === this._boardId) ? BACKLOG_ID : null;
  }

  private visibleTasks(columnId: string) {
    const query = this._layout.query;

    return (this._tasks.value[columnId] ?? []).filter((task) => matchesQuery({ task, query }));
  }
}
